import React, { useState } from 'react';
import { Card, CardContent } from '../../../Components/UI/card';
import {
  UserPlus as Source, Package as Product, MapPin as City, Edit3 as ExtraField, ThumbsDown as RejectionReason, UserX as InactiveReason, Building as Segment,
  Calendar as TaskType, Archive as RecordType, MapPinned, CircleX, Columns3Cog, Puzzle, ListOrdered
} from 'lucide-react';

// Import the consolidated MastersDialog component
import MastersDialog from '../Masters';
import OrderStagesDialog from '../OrderStagesDialog';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getActiveThreadTypesArr, userIsAdmin } from '../../../Store/Reducers';
import { getThreadModeTitle } from '../../../Utils/Utilities';
import { cn } from '../../../Lib/Cn';
import useIsMobile from '../../../Hooks/useIsMobile';

const MastersPage = () => {

  // Local state for dialog visibility and current master type
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentMasterType, setCurrentMasterType] = useState(null);
  const [orderStagesOpen, setOrderStagesOpen] = useState(false);
  const threadTypeArr = useSelector(getActiveThreadTypesArr);
  const currActiveThreadType = getThreadModeTitle(threadTypeArr);
  const isMobile = useIsMobile();
  const isAdmin = useSelector(userIsAdmin);
  const navigate = useNavigate();

  // Master data mapping for display
  const masterCategories = [
    {
      id: 'sources',
      title: 'Sources',
      description: 'Define and manage the channels from which inquiries are generated.',
      icon: Source,
      iconColorClass: !isMobile ? 'bg-sahi-primary-color' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    {
      id: 'products',
      title: 'Offerings',
      description: 'Maintain your catalog of offerings available for inquiries, orders and support tickets.',
      icon: Product,
      iconColorClass: !isMobile ? 'bg-sahi-primary-color' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    {
      id: 'cities',
      title: 'Cities',
      description: 'Add or update cities used across your CRM for addresses and regions.',
      icon: City,
      iconColorClass: !isMobile ? 'bg-sahi-primary-color' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    {
      id: 'connExtraFields',
      title: 'Extra Fields (Contacts)',
      description: 'Configure additional fields to be shown in contact entries.',
      icon: ExtraField,
      iconColorClass: !isMobile ? 'bg-sahi-primary-color' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    {
      id: 'thrExtraFields',
      title: `Extra Fields (${currActiveThreadType.typPlr})`,
      description: `Configure additional fields to be shown in ${currActiveThreadType.typPlr} entries.`,
      icon: ExtraField,
      iconColorClass: !isMobile ? 'bg-sahi-primary-color' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    // SWAPPED: Thread Stages moved up
    {
      id: 'threadStages',
      title: `${currActiveThreadType.typSng} Pipeline Stages`, // Longer title to ensure 2 lines
      description: `Change the ${currActiveThreadType.typSng} stages as per your requirement.`,
      icon: Columns3Cog,
      iconColorClass: !isMobile ? 'bg-sahi-primary-color' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    {
      id: 'orderStages',
      title: 'Order Stages',
      description: 'Add or remove the stages an order moves through.',
      icon: ListOrdered,
      iconColorClass: !isMobile ? 'bg-sahi-primary-color' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    {
      id: 'recordTypes',
      title: 'Task/Record Types',
      description: 'Categorize different types of records and tasks within the system.',
      icon: RecordType,
      iconColorClass: !isMobile ? 'bg-sahi-primary-color' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    {
      id: 'rejectionReasons',
      title: 'Failure Reasons', // Longer title to ensure 2 lines
      description: 'Track reasons why contacts reject your inquiries or proposals.',
      icon: RejectionReason,
      iconColorClass: !isMobile ? 'bg-sahi-red' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    {
      id: 'disqlReasons',
      title: 'Disqualification Reasons',
      description: 'Track reasons why connections disqualify your inquiries or proposals.',
      icon: CircleX,
      iconColorClass: !isMobile ? 'bg-sahi-red' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    // SWAPPED: Categories moved down
    {
      id: 'segments',
      title: 'Categories',
      description: 'Categories contacts based on predefined segments.',
      icon: Segment,
      iconColorClass: !isMobile ? 'bg-sahi-primary-color' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    {
      id: 'regions',
      title: 'Regions',
      description: 'Define and manage regions for region wise management of contacts.',
      icon: MapPinned,
      iconColorClass: !isMobile ? 'bg-sahi-primary-color' : 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
    },
    // Mobile only - on desktop, Integrations already has its own sidebar entry.
    // Hidden from the mobile sidebar menu itself, so this is now the only way to reach it on mobile.
    ...(isMobile && isAdmin ? [{
      id: 'integrations',
      title: 'Integrations',
      description: 'Connect external platforms.',
      icon: Puzzle,
      iconColorClass: 'bg-sahi-dark-blue/10 text-sahi-dark-blue',
      isRoute: true,
      path: '/integrations',
    }] : []),
  ];

  const handleCardClick = (master) => {
    if (master.isRoute) {
      navigate(master.path);
      return;
    }
    if (master.id === 'orderStages') {
      setOrderStagesOpen(true);
      return;
    }
    setCurrentMasterType(master.id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentMasterType(null);
  };

  return (
    <div>
      <div className={cn(
        "grid w-full gap-4",
        "grid-cols-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
        isMobile ? "px-0" : "py-4 px-0"
      )}>
        {masterCategories.map((master) => {
          const IconComponent = master.icon;

          return !isMobile ? (
            <Card
              key={master.id}
              className={`sahi-card !shadow-[0_2px_6px_rgba(0,0,0,0.04)] !rounded-xl !pb-3 cursor-pointer`}
              onClick={() => handleCardClick(master)}
            >
              <CardContent className="sahi-card-content !gap-5">
                <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 mb-1", master.iconColorClass)}>
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-text-color text-lg mb-1 leading-tight">
                    {master.title}
                  </h3>
                  <p className="text-zinc-500 text-sm leading-relaxed overflow-hidden text-ellipsis line-clamp-2">
                    {master.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card
              key={master.id}
              className={cn(
                "cursor-pointer transition-all active:scale-95",
                "bg-white border border-zinc-200/50 rounded-xl",
                "flex flex-col items-center justify-center text-center  h-full min-h-[95px]"
              )}
              style={{ borderRadius: "12px" }}
              onClick={() => handleCardClick(master)}
            >
              <CardContent className="p-0 flex flex-col items-center justify-center w-full">
                <div className="flex flex-col items-center text-center w-full">
                                <div className="flex flex-col items-center text-center gap-0.5">

                    <div className={cn(
                      "flex items-center justify-center rounded-xl w-9 h-9",
                      master.iconColorClass
                    )}>
                      <IconComponent className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="w-full px-1">
                    <h3 className="text-text-color break-words text-[12px] leading-[14px]">
                      {/* Logic to manually break lines for specific cards */}
                      {master.id === 'rejectionReasons' ? (
                        <>Failure<br />Reasons</>
                      ) : master.id === 'threadStages' ? (
                        <>{currActiveThreadType.typSng}<br />Stages</>
                      ) : master.id === 'connExtraFields' ? (
                        <>Extra Fields<br />(Contacts)</>
                      ) : master.id === 'thrExtraFields' ? (
                        <>Extra Fields<br />({currActiveThreadType.typPlr})</>
                      ) : master.id === 'recordTypes' ? (
                        <>Task/Record<br />Types</>
                      ) : master.id === 'disqlReasons' ? (
                        <>Disqualification<br />Reasons</>
                      ) : (
                        master.title
                      )}
                    </h3>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {currentMasterType && (
        <MastersDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          masterType={currentMasterType}
        />
      )}

      <OrderStagesDialog
        open={orderStagesOpen}
        onClose={() => setOrderStagesOpen(false)}
      />
    </div>
  );
};

export default MastersPage;